import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Calendar, Filter, Clock, ArrowLeft, Download } from 'lucide-react';
import html2canvas from 'html2canvas';

// Parse to local midnight to avoid UTC-shift bug:
// new Date('YYYY-MM-DD') gives UTC midnight, which in negative-offset timezones
// (e.g. America/Chicago UTC-5) lands on the prior calendar day.
const toLocalMidnight = (date) => {
    if (!date) return new Date();
    if (date instanceof Date) return new Date(date.getFullYear(), date.getMonth(), date.getDate());
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}/.test(date)) {
        const [y, m, d] = date.split('-').map(Number);
        return new Date(y, m - 1, d);
    }
    const d = new Date(date);
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

const addDays = (date, days) => {
    const d = toLocalMidnight(date);
    d.setDate(d.getDate() + days);
    return d;
};

const getDaysDiff = (start, end) => {
    const s = toLocalMidnight(start);
    const e = toLocalMidnight(end);
    return (e - s) / (1000 * 60 * 60 * 24);
};

const GanttView = ({ tasks = [], projectStartDate, onEditTask }) => {
    const [viewMode, setViewMode] = useState('Day');
    const [categoryFilter, setCategoryFilter] = useState('All');
    const [showHistory, setShowHistory] = useState(false);
    const scrollContainerRef = useRef(null);
    const printRef = useRef(null);

    const categories = ['All', ...new Set(tasks.map(t => t.category || 'General'))];
    const ROW_HEIGHT = 32;
    const COLUMN_WIDTH = 40;

    const pixelsPerDay = useMemo(() => {
        switch(viewMode) {
            case 'Day': return COLUMN_WIDTH;
            case 'Week': return COLUMN_WIDTH / 7;
            case 'Month': return COLUMN_WIDTH / 30;
            default: return COLUMN_WIDTH;
        }
    }, [viewMode]);

    const { timeline, gridHeaders, sortedTasks, criticalPathIds } = useMemo(() => {
        if (!projectStartDate && tasks.length === 0) 
            return { timeline: { start: new Date(), width: 0 }, gridHeaders: [], sortedTasks: [], criticalPathIds: new Set() };
        
        const sorted = [...tasks].sort((a,b) => new Date(a.startDate) - new Date(b.startDate));
        
        let startBound;
        if (showHistory) {
            const pStart = new Date(projectStartDate || new Date());
            const taskStarts = sorted.map(t => new Date(t.startDate));
            startBound = new Date(Math.min(pStart, ...taskStarts));
        } else {
            const activeTasks = sorted.filter(t => t.progress < 100);
            if (activeTasks.length > 0) {
                const activeStarts = activeTasks.map(t => new Date(t.startDate));
                startBound = new Date(Math.min(...activeStarts));
            } else {
                startBound = new Date();
            }
            startBound = addDays(startBound, -3);
        }

        const taskEnds = sorted.map(t => addDays(t.startDate, parseInt(t.duration) || 1));
        const maxDate = new Date(Math.max(addDays(new Date(), 30), ...taskEnds));
        
        let timelineStart = addDays(startBound, -7);
        let timelineEnd = addDays(maxDate, 30);
        
        const totalDays = Math.max(getDaysDiff(timelineStart, timelineEnd), 30);
        const totalWidth = totalDays * pixelsPerDay;

        const headers = [];
        let current = new Date(timelineStart);
        while (current <= timelineEnd) {
            headers.push(new Date(current));
            if (viewMode === 'Day') current = addDays(current, 1);
            else if (viewMode === 'Week') current = addDays(current, 7);
            else if (viewMode === 'Month') current.setMonth(current.getMonth() + 1);
        }

        const projectEndDate = new Date(Math.max(...taskEnds));
        const criticalIds = new Set();
        const taskMap = new Map(sorted.map(t => [t.id, t]));

        const checkCritical = (taskId) => {
            if (criticalIds.has(taskId)) return;
            criticalIds.add(taskId);
            const task = taskMap.get(taskId);
            if (!task || !task.dependencies) return;
            const taskStart = new Date(task.startDate);
            task.dependencies.forEach(depId => {
                const parent = taskMap.get(depId);
                if (parent) {
                    const parentEnd = addDays(parent.startDate, parseInt(parent.duration) || 1);
                    if (getDaysDiff(parentEnd, taskStart) <= 1) checkCritical(depId);
                }
            });
        };
        sorted.forEach(t => {
            const tEnd = addDays(t.startDate, parseInt(t.duration) || 1);
            if (getDaysDiff(tEnd, projectEndDate) <= 1) checkCritical(t.id);
        });

        return { timeline: { start: timelineStart, width: totalWidth }, gridHeaders: headers, sortedTasks: sorted, criticalPathIds: criticalIds };
    }, [tasks, projectStartDate, viewMode, pixelsPerDay, showHistory]);

    useEffect(() => {
        if (scrollContainerRef.current && !showHistory) {
            scrollContainerRef.current.scrollLeft = 0; 
        }
    }, [timeline.start, showHistory]);

    const displayTasks = useMemo(() => {
        if (categoryFilter === 'All') return sortedTasks;
        return sortedTasks.filter(t => t.category === categoryFilter);
    }, [sortedTasks, categoryFilter]);

    const getTaskColor = (task) => {
        const today = new Date(); today.setHours(0,0,0,0);
        const start = new Date(task.startDate);
        const end = addDays(start, parseInt(task.duration) || 1);
        if (task.progress === 100) return 'bg-green-500';
        if (end < today) return 'bg-red-500';
        if (start <= today && today < end) return 'bg-blue-500';
        if (criticalPathIds.has(task.id)) return 'bg-orange-400';
        return 'bg-gray-400';
    };

    const renderDependencies = () => {
        if (categoryFilter !== 'All') return null;
        return displayTasks.map(task => {
            const deps = task.dependencies || [];
            return deps.map(depId => {
                const parent = displayTasks.find(t => t.id === depId);
                if (!parent) return null;
                const pEndDay = getDaysDiff(timeline.start, addDays(parent.startDate, parseInt(parent.duration)||1));
                const cStartDay = getDaysDiff(timeline.start, task.startDate);
                const x1 = pEndDay * pixelsPerDay;
                const y1 = (displayTasks.indexOf(parent) * ROW_HEIGHT) + (ROW_HEIGHT/2);
                const x2 = cStartDay * pixelsPerDay;
                const y2 = (displayTasks.indexOf(task) * ROW_HEIGHT) + (ROW_HEIGHT/2);
                const path = `M ${x1} ${y1} L ${x1 + 10} ${y1} L ${x1 + 10} ${y2} L ${x2} ${y2}`;
                const isCriticalLink = criticalPathIds.has(task.id) && criticalPathIds.has(parent.id);
                return <path key={`${depId}-${task.id}`} d={path} stroke={isCriticalLink ? '#fb923c' : '#cbd5e1'} strokeWidth={isCriticalLink ? 2 : 1.5} fill="none" markerEnd={isCriticalLink ? "url(#arrow-orange)" : "url(#arrow-gray)"} />;
            });
        });
    };

    const handleExport = async () => {
        if (printRef.current) {
            const canvas = await html2canvas(printRef.current, { scrollX: 0, scrollY: 0, width: printRef.current.scrollWidth, height: printRef.current.scrollHeight });
            const link = document.createElement('a');
            link.download = 'schedule.png';
            link.href = canvas.toDataURL();
            link.click();
        }
    };

    return (
        <div className="flex flex-col h-full bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden select-none">
            <div className="p-3 border-b flex flex-col md:flex-row justify-between items-center bg-gray-50 gap-4">
                <div className="flex items-center gap-3">
                    <h3 className="font-bold text-gray-700 flex items-center gap-2"><Calendar className="text-blue-600"/> Schedule</h3>
                    <button onClick={() => setShowHistory(!showHistory)} className={`flex items-center gap-1 text-[10px] uppercase font-bold px-3 py-1.5 rounded-full border transition-all ${showHistory ? 'bg-blue-100 text-blue-700 border-blue-200' : 'bg-white text-gray-500 border-gray-200 hover:border-blue-300'}`}>
                        {showHistory ? <ArrowLeft size={10}/> : <Clock size={10}/>} {showHistory ? "History On" : "History Off"}
                    </button>
                </div>
                <div className="flex gap-4 items-center">
                    <button onClick={handleExport} className="p-2 bg-white border rounded text-gray-600 hover:text-blue-600 hover:border-blue-300" title="Download Image"><Download size={16}/></button>
                    <div className="flex items-center gap-2 bg-white px-2 rounded border h-8">
                        <Filter size={12} className="text-gray-400"/>
                        <select className="border-none text-xs font-bold py-1 outline-none cursor-pointer bg-transparent" value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}>
                            {categories.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </div>
                    <div className="flex bg-white rounded-lg border p-0.5">
                        {['Day', 'Week', 'Month'].map(m => (
                            <button key={m} onClick={() => setViewMode(m)} className={`px-3 py-1 text-xs font-bold rounded ${viewMode===m ? 'bg-blue-100 text-blue-700 shadow-sm' : 'text-gray-500 hover:bg-gray-50'}`}>{m}</button>
                        ))}
                    </div>
                </div>
            </div>

            <div className="flex-1 overflow-auto relative bg-white" ref={scrollContainerRef}>
                <div ref={printRef} style={{ width: `${Math.max(timeline.width + 200, 800)}px`, minWidth: '100%', position: 'relative', backgroundColor: 'white' }}>
                    <div className="flex border-b h-10 bg-gray-50 sticky top-0 z-30">
                        <div className="w-48 flex-shrink-0 border-r px-4 py-2 font-bold text-xs text-gray-500 bg-gray-50 sticky left-0 z-40 shadow-[2px_0_5px_rgba(0,0,0,0.05)] flex items-center">Task</div>
                        <div className="flex">
                            {gridHeaders.map((d, i) => (
                                <div key={i} style={{ width: COLUMN_WIDTH }} className={`flex-shrink-0 border-r text-[10px] text-center flex flex-col justify-center ${d.getDate()===1 || d.getDay()===1 ? 'bg-gray-100 font-bold text-gray-700' : 'text-gray-400'}`}>
                                    {viewMode === 'Day' && <span className="font-bold">{d.getDate()}</span>}
                                    {viewMode === 'Week' && <span className="font-bold">{d.getDate()}</span>}
                                    {viewMode === 'Month' && <span className="font-bold">{d.toLocaleDateString(undefined, {month:'short'})}</span>}
                                </div>
                            ))}
                        </div>
                    </div>
                    <svg className="absolute top-10 left-48 pointer-events-none z-0" style={{ width: timeline.width, height: displayTasks.length * ROW_HEIGHT }}>
                        <defs>
                            <marker id="arrow-gray" markerWidth="6" markerHeight="6" refX="6" refY="3" orient="auto"><path d="M0,0 L0,6 L6,3 z" fill="#cbd5e1" /></marker>
                            <marker id="arrow-orange" markerWidth="6" markerHeight="6" refX="6" refY="3" orient="auto"><path d="M0,0 L0,6 L6,3 z" fill="#fb923c" /></marker>
                        </defs>
                        {renderDependencies()}
                    </svg>
                    <div className="absolute top-0 bottom-0 border-l-2 border-red-500 border-dashed z-10 pointer-events-none opacity-40" style={{ left: (getDaysDiff(timeline.start, new Date()) * pixelsPerDay) + 192 }}></div>
                    <div className="relative z-10">
                        {displayTasks.map(task => {
                            const offset = getDaysDiff(timeline.start, task.startDate);
                            const dur = parseInt(task.duration) || 1;
                            const colorClass = getTaskColor(task);
                            return (
                                <div key={task.id} className="flex hover:bg-blue-50/30 group border-b border-gray-100 last:border-0 relative" style={{ height: ROW_HEIGHT }}>
                                    <div className="w-48 flex-shrink-0 border-r px-4 truncate text-xs font-medium text-gray-700 bg-white sticky left-0 z-20 group-hover:bg-blue-50/30 cursor-pointer flex items-center shadow-[2px_0_5px_rgba(0,0,0,0.02)]" onClick={() => onEditTask(task)}>{task.name}</div>
                                    <div className="flex-1 relative h-full">
                                        <div className="absolute inset-0 flex pointer-events-none">
                                            {gridHeaders.map((_, i) => <div key={i} style={{ width: COLUMN_WIDTH }} className="border-r h-full border-gray-50/50"></div>)}
                                        </div>
                                        <div onClick={() => onEditTask(task)} className={`absolute rounded-sm shadow-sm border border-white/20 cursor-pointer hover:brightness-90 flex items-center px-1 overflow-hidden text-[7px] leading-tight font-bold text-white whitespace-nowrap transition-all duration-200 ${colorClass}`} style={{ left: offset * pixelsPerDay, width: Math.max(dur * pixelsPerDay, 4), top: 4, bottom: 4 }}>
                                            {(viewMode === 'Day' || dur * pixelsPerDay > 30) && `${task.name}`}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};
export default GanttView;